/**
 * Root Shell Component
 * Auth guard + global UI (shortcuts, toasts, modals, help).
 * Used as a layout route element wrapping all authenticated routes.
 * @packageDocumentation
 */

import { lazy, Suspense, useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router';

import { useAuthStore, useConfigStore } from '@/stores';
import { SessionExpiredModal, LdaUnavailableModal } from '@/components/auth';
import { Toaster, AppLoader, TooltipProvider, KeyboardShortcutsDialog } from '@/components/ui';
import { useSessionCheck, useGlobalShortcuts, useSessionLifecycle, useTenantQueryParams } from '@/hooks';

const LazyHelpPanel = lazy(() => import('@/components/ui/HelpPanel/HelpPanel').then((m) => ({ default: m.HelpPanel })));

/**
 * Root shell layout route that provides authentication guard and global UI.
 * Replaces both the old `App` component's global UI and `ProtectedRoute` wrapper.
 *
 * @returns { JSX.Element } The root shell element.
 */
export const RootShell = () => {
  const location = useLocation();
  const { isLoading, isInitializing, sessionExpired, isAuthenticated } = useAuthStore();
  const { fetchConfig, isLoading: isConfigLoading, hasLoaded: hasConfigLoaded } = useConfigStore();

  useSessionCheck();
  useGlobalShortcuts();
  useSessionLifecycle();
  useTenantQueryParams();

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Show loading while config is being fetched
  if (isConfigLoading || !hasConfigLoaded) {
    return <AppLoader />;
  }

  // Show loading while auth is initializing (but not during reauth — the modal handles that)
  if ((isLoading || isInitializing) && !sessionExpired) {
    return <AppLoader />;
  }

  if (!isAuthenticated && !sessionExpired) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Outlet />
      <Toaster position="bottom-right" richColors closeButton />
      <KeyboardShortcutsDialog />
      <Suspense>
        <LazyHelpPanel />
      </Suspense>
      <SessionExpiredModal />
      <LdaUnavailableModal />
    </TooltipProvider>
  );
};
